import { router, useFocusEffect } from "expo-router";
import React, { useCallback, useState } from "react";
import {
  Alert,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

type SavedPublicBusiness = {
  businessId: string;
  slug: string;
  businessName: string;
  publicUrl: string;
  savedAt: string;
};

const SAVED_PUBLIC_BUSINESSES_KEY = "sokoGridSavedPublicBusinesses";

function cleanText(value: unknown) {
  return String(value ?? "").trim();
}

function getSavedTime(value: string) {
  const time = Date.parse(value);

  return Number.isFinite(time) ? time : 0;
}

function normalizeSavedBusiness(value: unknown): SavedPublicBusiness | null {
  if (!value || typeof value !== "object") return null;

  const item = value as Record<string, unknown>;
  const businessId = cleanText(item.businessId);
  const slug = cleanText(item.slug).replace(/^\/+/g, "").replace(/^p\//i, "");
  const businessName = cleanText(item.businessName) || "Business";
  const publicUrl = cleanText(item.publicUrl);
  const savedAt = cleanText(item.savedAt);

  if (!businessId && !slug && !publicUrl) return null;

  return {
    businessId,
    slug,
    businessName,
    publicUrl,
    savedAt,
  };
}

function dedupeAndSortSavedBusinesses(
  items: SavedPublicBusiness[]
): SavedPublicBusiness[] {
  const seenBusinessIds = new Set<string>();
  const seenSlugs = new Set<string>();
  const seenUrls = new Set<string>();

  return [...items]
    .sort((a, b) => getSavedTime(b.savedAt) - getSavedTime(a.savedAt))
    .filter((item) => {
      if (item.businessId && seenBusinessIds.has(item.businessId)) return false;
      if (item.slug && seenSlugs.has(item.slug)) return false;
      if (item.publicUrl && seenUrls.has(item.publicUrl)) return false;

      if (item.businessId) seenBusinessIds.add(item.businessId);
      if (item.slug) seenSlugs.add(item.slug);
      if (item.publicUrl) seenUrls.add(item.publicUrl);

      return true;
    });
}

function readSavedPublicBusinesses(): SavedPublicBusiness[] {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(SAVED_PUBLIC_BUSINESSES_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];

    if (!Array.isArray(parsed)) return [];

    return dedupeAndSortSavedBusinesses(
      parsed
        .map((item) => normalizeSavedBusiness(item))
        .filter((item): item is SavedPublicBusiness => item !== null)
    );
  } catch {
    return [];
  }
}

function writeSavedPublicBusinesses(items: SavedPublicBusiness[]) {
  if (typeof window === "undefined") return;

  window.localStorage.setItem(
    SAVED_PUBLIC_BUSINESSES_KEY,
    JSON.stringify(items)
  );
}

function savedBusinessesMatch(
  item: SavedPublicBusiness,
  target: SavedPublicBusiness
) {
  if (target.businessId && item.businessId === target.businessId) return true;
  if (target.slug && item.slug === target.slug) return true;
  if (target.publicUrl && item.publicUrl === target.publicUrl) return true;

  return false;
}

function formatSavedDate(savedAt: string) {
  const time = getSavedTime(savedAt);

  if (!time) return "Saved date unavailable";

  return `Saved ${new Date(time).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })}`;
}

function getProfileLabel(item: SavedPublicBusiness) {
  if (item.publicUrl) return item.publicUrl;
  if (item.slug) return `/p/${item.slug}`;

  return "Public profile";
}

function showMessage(title: string, message: string) {
  if (
    Platform.OS === "web" &&
    typeof window !== "undefined" &&
    typeof window.alert === "function"
  ) {
    window.alert(`${title}\n\n${message}`);
    return;
  }

  Alert.alert(title, message);
}

export default function SavedBusinessesPage() {
  const [savedBusinesses, setSavedBusinesses] = useState<SavedPublicBusiness[]>(
    []
  );
  const [loaded, setLoaded] = useState(false);

  useFocusEffect(
    useCallback(() => {
      setSavedBusinesses(readSavedPublicBusinesses());
      setLoaded(true);

      return undefined;
    }, [])
  );

  const openProfile = useCallback(async (item: SavedPublicBusiness) => {
    if (item.slug) {
      router.push({
        pathname: "/p/[slug]",
        params: { slug: item.slug },
      });
      return;
    }

    if (!item.publicUrl) {
      showMessage(
        "Profile unavailable",
        "This saved business does not have a profile link."
      );
      return;
    }

    try {
      await Linking.openURL(item.publicUrl);
    } catch {
      showMessage(
        "Profile unavailable",
        "This saved business profile could not be opened."
      );
    }
  }, []);

  const removeSavedBusiness = useCallback(
    (target: SavedPublicBusiness) => {
      const next = savedBusinesses.filter(
        (item) => !savedBusinessesMatch(item, target)
      );

      try {
        writeSavedPublicBusinesses(next);
        setSavedBusinesses(next);
      } catch {
        showMessage(
          "Remove failed",
          "This browser could not update your saved businesses."
        );
      }
    },
    [savedBusinesses]
  );

  const openDirectory = useCallback(() => {
    router.push("/directory" as any);
  }, []);

  const isNative = Platform.OS !== "web";

  return (
    <ScrollView
      style={styles.screen}
      contentContainerStyle={styles.content}
      showsVerticalScrollIndicator
    >
      <SafeAreaView style={styles.safeAreaContent}>
        <View style={styles.container}>
          <View style={styles.headerRow}>
            <View style={styles.headerText}>
              <Text style={styles.title}>Saved Businesses</Text>
              <Text style={styles.helper}>
                Saved businesses are stored on this browser/device.
              </Text>
            </View>

            <Pressable onPress={openDirectory} style={styles.directoryButton}>
              <Text style={styles.directoryButtonText}>Directory</Text>
            </Pressable>
          </View>

          {isNative ? (
            <View style={styles.emptyCard}>
              <Text style={styles.emptyTitle}>Saved Businesses</Text>
              <Text style={styles.emptyText}>
                Saved businesses are stored on the web browser where you saved
                them.
              </Text>
            </View>
          ) : !loaded ? (
            <View style={styles.emptyCard}>
              <Text style={styles.emptyTitle}>Loading saved businesses...</Text>
            </View>
          ) : savedBusinesses.length === 0 ? (
            <View style={styles.emptyCard}>
              <Text style={styles.emptyTitle}>No saved businesses yet.</Text>
              <Text style={styles.emptyText}>
                When you save a public business card, it will appear here.
              </Text>
            </View>
          ) : (
            <View style={styles.list}>
              {savedBusinesses.map((item) => (
                <View
                  key={`${item.businessId || item.slug || item.publicUrl}`}
                  style={styles.card}
                >
                  <View style={styles.cardHeader}>
                    <View style={styles.cardTitleBlock}>
                      <Text style={styles.businessName} numberOfLines={2}>
                        {item.businessName}
                      </Text>
                      <Text style={styles.savedDate}>
                        {formatSavedDate(item.savedAt)}
                      </Text>
                    </View>
                  </View>

                  <Text style={styles.profileLink} numberOfLines={2}>
                    {getProfileLabel(item)}
                  </Text>

                  <View style={styles.actionsRow}>
                    <Pressable
                      onPress={() => openProfile(item)}
                      style={({ pressed }) => [
                        styles.openButton,
                        pressed ? styles.pressed : null,
                      ]}
                    >
                      <Text style={styles.openButtonText}>Open Profile</Text>
                    </Pressable>

                    <Pressable
                      onPress={() => removeSavedBusiness(item)}
                      style={({ pressed }) => [
                        styles.removeButton,
                        pressed ? styles.pressed : null,
                      ]}
                    >
                      <Text style={styles.removeButtonText}>Remove</Text>
                    </Pressable>
                  </View>
                </View>
              ))}
            </View>
          )}
        </View>
      </SafeAreaView>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: "#f3f4f6",
  },
  content: {
    flexGrow: 1,
    width: "100%",
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 72,
  },
  safeAreaContent: {
    width: "100%",
  },
  container: {
    width: "100%",
    maxWidth: 920,
    alignSelf: "center",
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 16,
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: 28,
    lineHeight: 34,
    fontWeight: "900",
    color: "#111827",
  },
  helper: {
    marginTop: 6,
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "700",
    color: "#6b7280",
  },
  directoryButton: {
    minHeight: 40,
    paddingHorizontal: 14,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#d1d5db",
    backgroundColor: "#ffffff",
    alignItems: "center",
    justifyContent: "center",
  },
  directoryButtonText: {
    fontSize: 13,
    fontWeight: "900",
    color: "#111827",
  },
  emptyCard: {
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#ffffff",
    padding: 22,
    alignItems: "center",
  },
  emptyTitle: {
    fontSize: 18,
    lineHeight: 24,
    fontWeight: "900",
    color: "#111827",
    textAlign: "center",
  },
  emptyText: {
    marginTop: 8,
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "700",
    color: "#6b7280",
    textAlign: "center",
  },
  list: {
    gap: 12,
  },
  card: {
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#ffffff",
    padding: 16,
    shadowColor: "#000000",
    shadowOpacity: 0.05,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 6 },
    elevation: 2,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  cardTitleBlock: {
    flex: 1,
    minWidth: 0,
  },
  businessName: {
    fontSize: 18,
    lineHeight: 23,
    fontWeight: "900",
    color: "#111827",
  },
  savedDate: {
    marginTop: 4,
    fontSize: 12,
    lineHeight: 17,
    fontWeight: "800",
    color: "#6b7280",
  },
  profileLink: {
    marginTop: 12,
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "700",
    color: "#2563eb",
  },
  actionsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 14,
  },
  openButton: {
    minHeight: 42,
    flexGrow: 1,
    paddingHorizontal: 16,
    borderRadius: 12,
    backgroundColor: "#111827",
    alignItems: "center",
    justifyContent: "center",
  },
  openButtonText: {
    fontSize: 13,
    fontWeight: "900",
    color: "#ffffff",
  },
  removeButton: {
    minHeight: 42,
    paddingHorizontal: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#fecaca",
    backgroundColor: "#fff7f7",
    alignItems: "center",
    justifyContent: "center",
  },
  removeButtonText: {
    fontSize: 13,
    fontWeight: "900",
    color: "#b91c1c",
  },
  pressed: {
    opacity: 0.82,
  },
});
